import React, { useEffect, useState } from "react"
import propTypes from "prop-types"
import { Button, DropdownItem, DropdownMenu, DropdownToggle, Input, UncontrolledDropdown } from "reactstrap"
import { getDatePresetRange } from "utils/date-filters"
import "assets/scss/custom/components/list-filter-bar.scss"

const datePresets = [
  { value: "today", label: "Hoy" },
  { value: "week", label: "Semana" },
  { value: "month", label: "Mes" },
  { value: "year", label: "Año" },
  { value: "range", label: "Rango" },
]

const isAllOption = (option) => option.value === "all" || ["todos", "todas"].includes(option.label.toLowerCase())

const normalizeSelectedValues = (value) => {
  if (Array.isArray(value)) {
    return value
  }

  if (!value || value === "all") {
    return []
  }

  return [value]
}

const getSelectedValues = (filter, localSelectValues) => {
  return normalizeSelectedValues(localSelectValues[filter.name] ?? filter.value)
}

const ListFilterBar = ({
  dateFilter,
  dateLabel,
  onDateFilterChange,
  selects,
  onClear,
  autoApply,
}) => {
  const hasDateFilter = Boolean(dateFilter && onDateFilterChange)
  const hasFilters = hasDateFilter || selects.length > 0
  const [localDateFilter, setLocalDateFilter] = useState(dateFilter)
  const [localSelectValues, setLocalSelectValues] = useState(() =>
    selects.reduce((values, filter) => ({ ...values, [filter.name]: filter.value }), {})
  )
  const selectValueKey = selects.map((filter) => `${filter.name}:${filter.value}`).join("|")

  useEffect(() => {
    setLocalDateFilter(dateFilter)
  }, [dateFilter])

  useEffect(() => {
    setLocalSelectValues(selects.reduce((values, filter) => ({ ...values, [filter.name]: filter.value }), {}))
  }, [selectValueKey])

  if (!hasFilters) return null

  const handlePresetClick = (preset) => {
    let nextDateFilter

    if (preset === "range") {
      nextDateFilter = { ...localDateFilter, preset }
    } else {
      nextDateFilter = {
        preset,
        ...getDatePresetRange(preset),
      }
    }

    setLocalDateFilter(nextDateFilter)

    if (autoApply && hasDateFilter) {
      onDateFilterChange(nextDateFilter)
    }
  }

  const handleDateChange = (field, value) => {
    const nextDateFilter = {
      ...localDateFilter,
      preset: "range",
      [field]: value,
    }

    setLocalDateFilter(nextDateFilter)

    if (autoApply && hasDateFilter) {
      onDateFilterChange(nextDateFilter)
    }
  }

  const openDatePicker = (event) => {
    const input = event.currentTarget
    if (typeof input.showPicker === "function") {
      try {
        input.showPicker()
        return
      } catch (error) {
        input.focus()
        return
      }
    }

    input.focus()
  }

  const handleSelectChange = (name, value) => {
    setLocalSelectValues((prevState) => ({
      ...prevState,
      [name]: value,
    }))

    if (autoApply) {
      const filter = selects.find((item) => item.name === name)
      if (filter) {
        filter.onChange(value)
      }
    }
  }

  const handleSelectToggle = (filter, optionValue) => {
    const currentValues = normalizeSelectedValues(localSelectValues[filter.name] ?? filter.value)
    const nextValues = currentValues.includes(optionValue)
      ? currentValues.filter((value) => value !== optionValue)
      : [...currentValues, optionValue]

    handleSelectChange(filter.name, nextValues)
  }

  const handleApply = () => {
    if (hasDateFilter) {
      onDateFilterChange(localDateFilter)
    }

    selects.forEach((filter) => {
      if (localSelectValues[filter.name] !== filter.value) {
        filter.onChange(localSelectValues[filter.name])
      }
    })
  }

  const handleClear = () => {
    setLocalDateFilter({ preset: "", startDate: "", endDate: "" })
    setLocalSelectValues(selects.reduce((values, filter) => ({ ...values, [filter.name]: "all" }), {}))
    if (onClear) {
      onClear()
    }
  }

  return (
    <div className="list-filter-bar">
      {hasDateFilter && (
        <div className="list-filter-group list-filter-date-group" aria-label={dateLabel}>
          <span className="list-filter-icon">
            <i className="fa fa-calendar" />
          </span>
          <div className="list-filter-presets">
            {datePresets.map((preset) => (
              <Button
                key={preset.value}
                type="button"
                color="light"
                className={`list-filter-preset ${localDateFilter?.preset === preset.value ? "active" : ""}`}
                onClick={() => handlePresetClick(preset.value)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <div className="list-filter-range">
            <Input
              type="date"
              bsSize="sm"
              value={localDateFilter?.startDate || ""}
              onClick={openDatePicker}
              onFocus={openDatePicker}
              onChange={(event) => handleDateChange("startDate", event.target.value)}
            />
            <span>-</span>
            <Input
              type="date"
              bsSize="sm"
              value={localDateFilter?.endDate || ""}
              onClick={openDatePicker}
              onFocus={openDatePicker}
              onChange={(event) => handleDateChange("endDate", event.target.value)}
            />
          </div>
        </div>
      )}

      {selects.map((filter) => {
        const visibleOptions = filter.options.filter((option) => !isAllOption(option))
        const selectedValues = getSelectedValues(filter, localSelectValues)
        const selectedOptions = visibleOptions.filter((option) => selectedValues.includes(option.value))
        const selectedLabel = selectedOptions.length === 0
          ? filter.label
          : selectedOptions.length === 1
          ? selectedOptions[0].label
          : `${selectedOptions.length} seleccionados`

        return (
          <div key={filter.name} className="list-filter-group list-filter-select-group">
            <span className="list-filter-icon">
              <i className={filter.icon || "fa fa-filter"} />
            </span>
            <UncontrolledDropdown className="list-filter-select-dropdown">
              <DropdownToggle
                caret
                color="light"
                size="sm"
                className={`list-filter-select-toggle ${selectedOptions.length ? "active" : ""}`}
                aria-label={filter.label}
              >
                {selectedLabel}
              </DropdownToggle>
              <DropdownMenu>
                {visibleOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value)

                  return (
                    <DropdownItem
                      key={option.value}
                      toggle={false}
                      className={isSelected ? "selected" : ""}
                      onClick={() => handleSelectToggle(filter, option.value)}
                    >
                      <span className="list-filter-option-check">
                        {isSelected && <i className="fa fa-check" />}
                      </span>
                      <span>{option.label}</span>
                    </DropdownItem>
                  )
                })}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        )
      })}

      {onClear && (
        <Button type="button" color="light" className="list-filter-clear" onClick={handleClear} title="Limpiar filtros">
          <i className="fa fa-times" />
        </Button>
      )}

      {!autoApply && (
        <Button type="button" color="primary" className="list-filter-apply" 
          onClick={handleApply} title="Aplicar filtros">
          <i className="fa fa-check" />
        </Button>
      )}
    </div>
  )
}

ListFilterBar.propTypes = {
  dateFilter: propTypes.shape({
    preset: propTypes.string,
    startDate: propTypes.string,
    endDate: propTypes.string,
  }),
  dateLabel: propTypes.string,
  onDateFilterChange: propTypes.func,
  autoApply: propTypes.bool,
  selects: propTypes.arrayOf(
    propTypes.shape({
      icon: propTypes.string,
      label: propTypes.string.isRequired,
      name: propTypes.string.isRequired,
      onChange: propTypes.func.isRequired,
      options: propTypes.arrayOf(
        propTypes.shape({
          label: propTypes.string.isRequired,
          value: propTypes.string.isRequired,
        })
      ).isRequired,
      value: propTypes.oneOfType([propTypes.string, propTypes.arrayOf(propTypes.string)]).isRequired,
    })
  ),
  onClear: propTypes.func,
}

ListFilterBar.defaultProps = {
  dateLabel: "Filtrar por fecha",
  autoApply: true,
  selects: [],
}

export default ListFilterBar
